import React from 'react';
import ReactDOM from 'react-dom/client';
import html2canvas from 'html2canvas';
import DrillHoleMap from '../../components/DrillHoleMap';
import { HOLE_INSET_MAP } from './templateConfig';

const REPORT_STATUS_FILTERS = {
  Complete: true,
  'In Progress': true,
  Planned: false,
  Lost: true,
};

/**
 * Capture a drill-hole map DOM element as a PNG data URL.
 */
export async function captureDrillHoleMapPng(element, options = {}) {
  if (!element) {
    throw new Error('Map element is required for capture');
  }

  const {
    scale = 2,
    backgroundColor = '#1f2937',
    settleMs = 300,
  } = options;

  if (settleMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, settleMs));
  }

  const canvas = await html2canvas(element, {
    useCORS: true,
    allowTaint: true,
    backgroundColor,
    scale,
  });

  return canvas.toDataURL('image/png');
}

/**
 * Mount DrillHoleMap in export mode, wait for tiles/markers, capture, and clean up.
 */
export async function captureDrillHoleMapSnapshot(container, mapProps, options = {}) {
  if (!container) {
    throw new Error('Map capture container is required');
  }

  const { tileWaitMs = 800, captureWidthPx = 960, captureHeightPx = 540 } = options;
  const {
    holes,
    zoneColorMap,
    zoneFilters,
    focusSpanM = null,
    focusCenter = null,
    highlightHoleId = null,
    exportBoundsHoles = null,
    exportViewport = null,
  } = mapProps;

  container.innerHTML = '';

  const mount = document.createElement('div');
  mount.style.width = `${captureWidthPx}px`;
  mount.style.height = `${captureHeightPx}px`;
  mount.style.position = 'relative';
  mount.style.overflow = 'hidden';
  mount.style.background = '#1f2937';
  container.appendChild(mount);

  let resolveReady;
  const readyPromise = new Promise((resolve) => {
    resolveReady = resolve;
  });

  const root = ReactDOM.createRoot(mount);

  root.render(
    React.createElement(DrillHoleMap, {
      data: holes,
      filterType: 'zone',
      zoneColorMap,
      zoneFilters,
      statusFilters: REPORT_STATUS_FILTERS,
      exportMode: true,
      showLegend: false,
      focusSpanM,
      focusCenter,
      highlightHoleId,
      exportBoundsHoles,
      exportViewport,
      onExportReady: resolveReady,
    })
  );

  await readyPromise;

  if (tileWaitMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, tileWaitMs));
  }

  const mapEl = mount.querySelector('.drill-hole-map');
  if (!mapEl) {
    root.unmount();
    container.innerHTML = '';
    throw new Error('Map element not found after render');
  }

  try {
    return await captureDrillHoleMapPng(mapEl, { settleMs: 150 });
  } finally {
    root.unmount();
    container.innerHTML = '';
  }
}

/**
 * Capture a square inset map centered on one hole at a fixed ground span.
 * Shows surrounding campaign holes for context (mini progress map).
 */
export async function captureHoleInsetMapSnapshot(
  container,
  { hole, campaignHoles, zoneColorMap, zoneFilters, spanM = HOLE_INSET_MAP.spanM },
  options = {}
) {
  const lat = Number(hole?.latitude);
  const lon = Number(hole?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  const captureSizePx = options.captureSizePx ?? HOLE_INSET_MAP.capturePx;
  const holes =
    Array.isArray(campaignHoles) && campaignHoles.length > 0 ? campaignHoles : [hole];

  return captureDrillHoleMapSnapshot(
    container,
    {
      holes,
      zoneColorMap: zoneColorMap || {},
      zoneFilters: zoneFilters || {},
      focusSpanM: spanM,
      focusCenter: { lat, lon },
      highlightHoleId: hole.hole_id,
    },
    {
      captureWidthPx: captureSizePx,
      captureHeightPx: captureSizePx,
      tileWaitMs: options.tileWaitMs ?? 650,
    }
  );
}

/**
 * Download map PNG (used by Drilling Dashboard export button).
 */
export async function downloadDrillHoleMapPng(element, filename) {
  const dataUrl = await captureDrillHoleMapPng(element);
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
